import { css } from "@emotion/react";
import React from "react";

export const AnimatedWrapper: React.FC<{
    isVisible: boolean;
    children: React.ReactNode;
}> = ({ isVisible, children }) => {
    const [shouldRender, setShouldRender] = React.useState(isVisible);
    const [height, setHeight] = React.useState<number>(0);
    const [isAnimating, setIsAnimating] = React.useState(false);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const innerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (isVisible) {
            setShouldRender(true);
        } else {
            setIsAnimating(true);
        }
    }, [isVisible]);

    React.useEffect(() => {
        if (shouldRender && innerRef.current) {
            // Measure height when content is rendered but hidden
            const measureHeight = () => {
                if (innerRef.current) {
                    const computedHeight = innerRef.current.scrollHeight;
                    setHeight(computedHeight);

                    if (isVisible && !isAnimating) {
                        // Start animation after height is measured
                        setTimeout(() => setIsAnimating(true), 10);
                    }
                }
            };

            // Initial measurement
            measureHeight();
        }
    }, [shouldRender, children, isVisible, isAnimating]);

    const handleTransitionEnd = () => {
        if (!isVisible) {
            setShouldRender(false);
            setIsAnimating(false);
        } else {
            setIsAnimating(false);
        }
    };

    // Always render when shouldRender is true, even when not visible (for height measurement)
    if (!shouldRender && !isVisible) {
        return null;
    }

    return (
        <div
            ref={contentRef}
            css={css`
                overflow: hidden;
                transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                opacity: ${isVisible && isAnimating ? 1 : 0};
                height: ${isVisible && isAnimating ? `${height}px` : "0px"};
                margin-bottom: ${isVisible && isAnimating ? "16px" : "0px"};
            `}
            onTransitionEnd={handleTransitionEnd}
        >
            <div
                ref={innerRef}
                css={css`
                    transform: ${isVisible && isAnimating ? "translateY(0)" : "translateY(-20px)"};
                    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                    padding: 12px 0;
                    opacity: ${shouldRender ? 1 : 0};
                `}
            >
                {children}
            </div>
        </div>
    );
};
